import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { TrackingDeviceAssignment, User, UserProfilePicture } from '../../../types/entity.types';

import useToaster from '../../../hooks/use-toaster/useToaster';

import { fileToBase64, getClassNames, noOp, retrieveFromLocalStorage } from '../../../utils/util';

import Button from '../button/Button';
import FilePicker from '../input/file-picker/FilePicker';
import PasswordInput from '../input/password-input/PasswordInput';
import DeviceAssignmentModal from '../modal/device-assignment-modal/DeviceAssignmentModal';
import ProfilePicture from '../profile-picture/ProfilePicture';
import PasswordValidation from '../validation/password-validation/PasswordValidation';
import UsernameValidation from '../validation/username-validation/UsernameValidation';

import './profile.scss';

type Props = {
  show: boolean;
  user?: User;
  signOut: () => void;
  updateSystemUser: (user: User) => void;
  createTrackingDeviceAssignment: (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => void;
  retrieveTrackingDeviceAssignments: (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => void;
};

const Profile = ({
  show,
  user,
  signOut,
  updateSystemUser,
  createTrackingDeviceAssignment,
  retrieveTrackingDeviceAssignments
}: Props) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationPassword, setConfirmationPassword] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [enteringUsername, setEnteringUsername] = useState(false);
  const [enteringPassword, setEnteringPassword] = useState(false);
  const [validUsername, setValidUsername] = useState(true);
  const [validPassword, setValidPassword] = useState(true);

  const [showDeviceAssignmentModal, setShowDeviceAssignmentModal] = useState(false);

  const fileRef = useRef<HTMLInputElement | null>(null);

  const { showToaster } = useToaster();

  useEffect(() => {
    if (user && show) {
      const { username, email } = user;
      setUsername(username ?? '');
      setEmail(email ?? '');
      setPassword('');
      setConfirmationPassword('');
    }
  }, [user, show]);

  const systemUserExternalReference: string | undefined = useMemo(() => retrieveFromLocalStorage('user'), []);

  const handleSaveClick = async () => {
    const externalReference = retrieveFromLocalStorage('user');
    let profilePicture: UserProfilePicture;

    if (file) {
      profilePicture = {
        base64String: await fileToBase64(file),
        name: file.name
      };
    } else {
      profilePicture = { uuid: user?.profilePicture as string };
    }

    const updatedUser: User = {
      externalReference,
      username,
      email,
      password: password ? password : undefined,
      profilePicture
    };
    updateSystemUser(updatedUser);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const file: File | undefined = files[0];

    if (!file) return;

    if (file.size < 5000000) {
      setFile(file);
      return;
    }

    showToaster('error', 'Profile picture must be less than 5MB');
  };

  return (
    <>
      <div className={getClassNames('profile', { show: show && !showDeviceAssignmentModal })} data-testid="profile">
        <p className="profile__header">My Profile</p>

        <div className="profile__image-container" data-testid="profile-image">
          <ProfilePicture
            src={file ? file : (user?.profilePicture as string)}
            alt={`${user?.username} - profile picture`}
          />
          <FilePicker
            fileRef={fileRef}
            fileType="image/*"
            onChange={handleFileChange}
            triggerElement={
              <div
                className="profile__image-container__icon"
                onClick={() => fileRef.current?.click()}
                data-testid="profile-upload-icon"
              >
                <FontAwesomeIcon icon={faPlus} />
              </div>
            }
          />
        </div>

        <div className="profile__input">
          <label htmlFor="profile-username">Username:</label>
          <input
            value={username}
            onFocus={() => setEnteringUsername(true)}
            onBlur={() => setEnteringUsername(false)}
            onChange={({ target }) => setUsername(target.value)}
            id="profile-username"
          />
          {(enteringUsername || (username && username !== user?.username)) && (
            <UsernameValidation username={username} setValidUsername={setValidUsername} />
          )}
        </div>

        <div className="profile__input">
          <label htmlFor="profile-email">Email Address:</label>
          <input value={email} onChange={({ target }) => setEmail(target.value)} id="profile-email" />
        </div>

        <div className="profile__input">
          <label htmlFor="password">Password:</label>
          <PasswordInput
            id="password"
            value={password}
            onFocus={() => setEnteringPassword(true)}
            onBlur={() => setEnteringPassword(false)}
            onChange={({ target }) => setPassword(target.value)}
            profile
          />
        </div>

        {(enteringPassword || password.length > 0) && (
          <div className="profile__input">
            <label htmlFor="confirmation-password">Confirm Password:</label>
            <PasswordInput
              id="confirmation-password"
              value={confirmationPassword}
              onChange={({ target }) => setConfirmationPassword(target.value)}
              profile
            />
            <PasswordValidation
              password={password}
              confirmationPassword={confirmationPassword}
              setValidPassword={setValidPassword}
            />
          </div>
        )}

        <div className="profile__device-id">
          <div className="profile__input">
            <label htmlFor="device-id">Device ID</label>
            <input value={user?.deviceId ?? ''} onChange={noOp} disabled />
          </div>
          <Button
            text="More info..."
            onClick={() => setShowDeviceAssignmentModal(true)}
            disabled={!systemUserExternalReference}
          />
        </div>

        <div className="profile__button-container">
          <Button text="SAVE" onClick={handleSaveClick} disabled={!validUsername || (!!password && !validPassword)} />
          <hr className="profile__divider" />
          <Button text="SIGN OUT" onClick={signOut} />
        </div>
      </div>

      <DeviceAssignmentModal
        systemUserExternalReference={systemUserExternalReference!}
        show={showDeviceAssignmentModal}
        close={() => setShowDeviceAssignmentModal(false)}
        createTrackingDeviceAssignment={createTrackingDeviceAssignment}
        retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
      />
    </>
  );
};

export default Profile;
